import { Link } from 'react-router-dom';
import { useSession } from '@/hooks/useSession';
import { SiteSwitcher } from '@/components/SiteSwitcher';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

type Props = {
  className?: string;
  /** 紧凑模式：嵌入页面顶部时不重复大 Card 标题 */
  compact?: boolean;
};

/**
 * 站点相关页面主体顶部的「工作站点」切换条（由原全局顶栏迁入）。
 */
export function SiteScopeBar({ className, compact }: Props) {
  const { currentSiteName, currentSiteId } = useSession();

  if (compact) {
    return (
      <div
        className={cn(
          'flex flex-col gap-3 rounded-xl border border-slate-200/90 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between',
          className,
        )}
      >
        <div className="min-w-0">
          <p className="text-xs font-semibold text-slate-700">当前工作站点</p>
          <p className="mt-0.5 text-[11px] leading-relaxed text-slate-500">
            本页数据与配置按所选站点隔离
            {currentSiteName && currentSiteId > 0 ? (
              <>
                {' · '}
                <span className="font-medium text-slate-700">{currentSiteName}</span>
                <span className="font-mono text-slate-400"> #{currentSiteId}</span>
              </>
            ) : null}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
          <SiteSwitcher idSuffix="-scope" className="min-w-[12rem] sm:min-w-[14rem]" />
          <Link
            to="/sites"
            className="whitespace-nowrap text-xs font-medium text-teal-700 underline decoration-teal-700/30 underline-offset-2 hover:text-teal-800"
          >
            站点管理
          </Link>
        </div>
      </div>
    );
  }

  return (
    <Card
      title="当前工作站点"
      className={className}
      titleExtra={
        <Link to="/sites" className="text-xs font-medium text-teal-700 hover:text-teal-800">
          管理站点
        </Link>
      }
    >
      <p className="mb-3 text-sm text-slate-600">
        以下配置与数据均按所选站点隔离；切换站点后本页内容将自动刷新。
      </p>
      <SiteSwitcher idSuffix="-scope" />
      {currentSiteName && currentSiteId > 0 ? (
        <p className="mt-2 text-xs text-slate-500">
          已选：<span className="font-medium text-slate-800">{currentSiteName}</span>
          <span className="ml-1 font-mono">#{currentSiteId}</span>
        </p>
      ) : null}
    </Card>
  );
}
